import {
  RadioCheckedIcon,
  RadioUncheckedIcon,
  UncheckedCircleIcon,
} from 'components/Icons';
import RevxFlexLayout, { Props } from 'components/RevxFlexLayout';
import { cloneDeep, isArray } from 'lodash-es';
import { useEffect, useState } from 'react';
import './index.less';

export type RevxCheckboxPropsOption = {
  children?:
    | React.ReactElement
    | (React.ReactElement | null)[]
    | React.ReactText;
  otherChildren?:
    | React.ReactElement
    | (React.ReactElement | null)[]
    | React.ReactText;
  value: string | number;
  disabled?: boolean;
};

export type RevxCheckboxProps = {
  unCheckIconSize?: 'big' | 'small';
  value?: (string | number)[];
  disabled?: boolean;
  onChange?: (value: Array<string | number>) => void;
  options: RevxCheckboxPropsOption[];
  outSideRevxLayoutProps?: Props;
  inSideRevxLayoutProps?: Props;
  iconSequence?: 'first' | 'last';
  maxLength?: number;
  minLength?: number;
  hidden?: boolean;
};

export const RevxCheckbox = ({
  maxLength,
  minLength,
  value,
  outSideRevxLayoutProps,
  inSideRevxLayoutProps,
  options,
  onChange,
  iconSequence = 'first',
  unCheckIconSize = 'big',
  disabled,
  hidden,
}: RevxCheckboxProps) => {
  const [curValue, setCurValue] = useState<Array<string | number>>(value || []);

  useEffect(() => {
    // controlled mode
    if (value !== undefined && isArray(value)) {
      setCurValue(value || []);
    }
  }, [value]);

  const onCurChange = (e: string | number) => {
    if (disabled) {
      return false;
    }

    let payloadValue = cloneDeep(curValue || []);
    if (payloadValue.includes(e)) {
      if (!minLength || payloadValue.length > minLength) {
        payloadValue = payloadValue?.filter((item) => item !== e);
      }
    } else {
      payloadValue?.push(e);
    }
    if (maxLength && payloadValue.length > maxLength) {
      payloadValue.shift();
    }
    if (typeof onChange === 'function') {
      onChange(payloadValue);
    }
    setCurValue(payloadValue);
  };

  const iconRender = (item: RevxCheckboxPropsOption) => {
    if (iconSequence === 'first') {
      return (
        <>
          <>{item?.otherChildren}</>
          {curValue.includes(item.value) ? (
            <RadioCheckedIcon />
          ) : (
            <>
              {unCheckIconSize === 'big' ? (
                <RadioUncheckedIcon />
              ) : (
                <UncheckedCircleIcon />
              )}
            </>
          )}
          <>{item?.children}</>
        </>
      );
    } else {
      return (
        <>
          <>{item?.children}</>
          {curValue.includes(item.value) ? (
            <RadioCheckedIcon />
          ) : (
            <>
              {unCheckIconSize === 'big' ? (
                <RadioUncheckedIcon />
              ) : (
                <UncheckedCircleIcon />
              )}
            </>
          )}
          <>{item?.otherChildren}</>
        </>
      );
    }
  };

  const itemRender = (item: RevxCheckboxPropsOption) => {
    if (inSideRevxLayoutProps?.align && inSideRevxLayoutProps.justify) {
      return (
        <RevxFlexLayout {...inSideRevxLayoutProps}>
          {iconRender(item)}
        </RevxFlexLayout>
      );
    } else {
      return <div {...inSideRevxLayoutProps}>{iconRender(item)}</div>;
    }
  };

  return (
    <RevxFlexLayout
      {...outSideRevxLayoutProps}
      className={`revx-checkbox-group ${outSideRevxLayoutProps?.className}`}
      hidden={hidden}
    >
      {options.map((item) => (
        <div
          className={`revx-checkbox-group-item ${
            curValue?.includes(item.value)
              ? 'revx-checkbox-group-item-active'
              : ''
          } ${
            disabled || item.disabled ? 'revx-checkbox-group-item-disabled' : ''
          }`}
          onClick={() => !item.disabled && onCurChange(item.value)}
          key={item.value}
        >
          {itemRender(item)}
        </div>
      ))}
    </RevxFlexLayout>
  );
};
